import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { getPutten, zoekPutOpmerking } from '../api/api';
import { fmtId, fmtDatumNL } from '../util/format';
import { gebiedStijl } from '../util/gebiedstijl';
import Klokje from '../components/Klokje';

/* De puttenlijst, herbouwd rond drie afspraken (26-08-2026):

   1. Status is een gekleurde stip met gewone tekst, geen gevulde pil. Na de
      migratie is "Niet gekoppeld" de norm voor bijna alle putten; een lijst
      die voor negentig procent knalrood is zegt niets meer. De kleur zit in
      de stip, de tekst blijft rustig. Het gebiedschip hield wél zijn kleur:
      daar ís de kleur de betekenis (dezelfde als op de kaart).

   2. Filteren is opgesplitst naar aard van de kolom. Categorieën (gebieden,
      status, waterstaatswerken) zitten in één paneel rechts; kolommen met
      vrije tekst of nummers (coördinaat, perceel, houder) hebben een
      vergrootglas in de kop dat een deeltreffer-zoekveldje opent — "BLE01"
      vindt alle percelen in Baarle. Aanvinken betekent tonen (de oude
      uitsluitingslijst deed niets zodra een kolom één waarde had), en elke
      waarde toont hoeveel rijen erachter zitten.

   3. Alles wat filtert is zichtbaar in de "Gefilterd:"-regel boven de tabel,
      elk met een kruisje. Kolomkoppen sorteren met één klik.

   De grote zoekbalk zoekt over putnummer, coördinaat, perceel en houder, en
   via de server ook in het migratieblok van de aantekening — uitsluitend dat
   blok, zodat een oud WBD- of kaartnummer een put terugvindt zonder dat er
   ooit in latere werkopmerkingen wordt gezocht. Een treffer uit de migratie
   krijgt een grijs merkje achter het putnummer met de gevonden regel als
   tooltip. */

const STATUS_KLEUR = {
  'Gekoppeld': 'var(--pl-groen)',
  'Gekoppeld (toekomstig)': 'var(--pl-groen)',
  'Niet gekoppeld': 'var(--pl-rood)',
  'Afdichtingsprocedure': 'var(--pl-amber)',
  'Afgedicht': 'var(--pl-grijs)',
};

/* De boorstatus komt kant-en-klaar uit de backend (boorstatus.py: aanlegdatum,
   gemeten diepte of bevestiging maakt Geboord, anders de te-boren-vlag). Oranje voor "Nog te boren" is dezelfde betekenis als overal
   elders in dit project: een put die nog geboord moet worden. "Onbekend" krijgt
   geen kleur maar een holle stip -- het is de afwezigheid van een gegeven, geen
   toestand om de aandacht op te vestigen. */
const AANLEG_KLEUR = {
  'Geboord': 'var(--pl-groen)',
  'Nog te boren': 'var(--pl-amber)',
  'Onbekend': null,
};

/* Eén klein, herbruikbaar zoekveld-in-de-kop. */
const LoepIcoon = () => (
  <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2"
       strokeLinecap="round" width="13" height="13">
    <circle cx="8.5" cy="8.5" r="5.5" /><path d="M12.8 12.8 17 17" />
  </svg>
);

const PIJLEN = {
  geen: (
    <svg viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.6"
         strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
      <path d="M4.2 5.4 7 2.6l2.8 2.8M4.2 8.6 7 11.4l2.8-2.8" />
    </svg>
  ),
  asc: (
    <svg viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2"
         strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
      <path d="M3.4 8.8 7 5.2l3.6 3.6" />
    </svg>
  ),
  desc: (
    <svg viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2"
         strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
      <path d="M3.4 5.2 7 8.8l3.6-3.6" />
    </svg>
  ),
};

const markeer = (tekst, zoek) => {
  const t = String(tekst ?? '');
  if (!zoek) return t;
  const i = t.toLowerCase().indexOf(zoek.toLowerCase());
  if (i < 0) return t;
  return (
    <>
      {t.slice(0, i)}<mark>{t.slice(i, i + zoek.length)}</mark>{t.slice(i + zoek.length)}
    </>
  );
};

export default function Putten() {
  const [putten, setPutten] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toonDiepteDetails, setToonDiepteDetails] = useState(false);
  const [toonWaterstaatswerken, setToonWaterstaatswerken] = useState(false);
  const [afgedichtOpen, setAfgedichtOpen] = useState(false);

  const [zoek, setZoek] = useState('');
  const [migratieTreffers, setMigratieTreffers] = useState(new Map());
  const [paneelOpen, setPaneelOpen] = useState(false);
  const [filters, setFilters] = useState({});   // key -> Set van getoonde waarden
  const [kopzoek, setKopzoek] = useState({});    // key -> deeltreffer-tekst
  const [kopOpen, setKopOpen] = useState(null);  // welk loepje staat open
  const [sort, setSort] = useState(null);        // { key, dir }
  const navigate = useNavigate();
  const zoekTimer = useRef(null);

  useEffect(() => { laad(); }, []);

  const laad = async () => {
    setLoading(true);
    try {
      const res = await getPutten();
      setPutten(res.data);
    } finally {
      setLoading(false);
    }
  };

  /* De servertreffers in het migratieblok, met een kleine adempauze zodat
     niet elke toetsaanslag een verzoek wordt. */
  useEffect(() => {
    if (zoekTimer.current) clearTimeout(zoekTimer.current);
    const q = zoek.trim();
    if (q.length < 2) { setMigratieTreffers(new Map()); return; }
    zoekTimer.current = setTimeout(async () => {
      try {
        const res = await zoekPutOpmerking(q);
        setMigratieTreffers(new Map(res.data.map(r => [r.put_id, r.regel])));
      } catch {
        setMigratieTreffers(new Map());
      }
    }, 250);
    return () => clearTimeout(zoekTimer.current);
  }, [zoek]);

  const statusLabel = (p, isAfgedicht) => {
    if (isAfgedicht) return 'Afgedicht';
    if (p.afdichting_gestart_datum) return 'Afdichtingsprocedure';
    if (p.gekoppeld_vanaf) return 'Gekoppeld (toekomstig)';
    if (!p.actief_gekoppeld) return 'Niet gekoppeld';
    return 'Gekoppeld';
  };

  const gebiedenKort = (p) => (p.gebieden || []).map(g => gebiedStijl(g.type).kort || g.naam);
  const wswKort = (p) => (p.waterstaatswerken || []).map(g => gebiedStijl(g.type).kort || g.naam);

  /* ── filteren ─────────────────────────────────────────────────────────── */

  const PANEEL_FACETTEN = [
    { key: 'gebieden', label: 'Kwetsbare gebieden', waarde: gebiedenKort,
      noot: 'De andere typen verschijnen hier vanzelf zodra ze bij een put voorkomen.' },
    ...(toonWaterstaatswerken
      ? [{ key: 'waterstaatswerken', label: 'Waterstaatswerken', waarde: wswKort }]
      : []),
    { key: 'status', label: 'Status', waarde: (p) => [p._status] },
    { key: 'boorstatus', label: 'Boorstatus',
      waarde: (p) => [p.boorstatus || 'Onbekend'] },
  ];

  const KOPZOEK_VELDEN = {
    coord: { label: 'RD x, y', tekst: p => `${Math.round(p.x_rd)}, ${Math.round(p.y_rd)}` },
    perceel: { label: 'Perceel', tekst: p => p.perceel || '' },
    houder: { label: 'Houder', tekst: p => p.houder_naam || '' },
  };

  const zoekTreffer = (p) => {
    const q = zoek.trim();
    if (!q) return { raak: true };
    const velden = [
      ['id', `${p.put_id} ${fmtId(p.put_id)}`],
      ['coord', `${Math.round(p.x_rd)}, ${Math.round(p.y_rd)}`],
      ['perceel', p.perceel || ''],
      ['houder', p.houder_naam || ''],
    ];
    for (const [waar, v] of velden) {
      if (v.toLowerCase().includes(q.toLowerCase())) return { raak: true, waar };
    }
    if (migratieTreffers.has(p.put_id)) {
      return { raak: true, waar: 'migratie', regel: migratieTreffers.get(p.put_id) };
    }
    return { raak: false };
  };

  const doorFilters = (p, negeer) => {
    if (!zoekTreffer(p).raak) return false;
    for (const f of PANEEL_FACETTEN) {
      if (f.key === negeer) continue;
      const g = filters[f.key];
      if (!g || !g.size) continue;
      if (!f.waarde(p).some(v => g.has(v))) return false;
    }
    for (const [k, def] of Object.entries(KOPZOEK_VELDEN)) {
      const z = kopzoek[k];
      if (z && !def.tekst(p).toLowerCase().includes(z.toLowerCase())) return false;
    }
    return true;
  };

  const zetFilter = (key, waarde) => {
    setFilters(prev => {
      const next = { ...prev };
      const s = new Set(next[key] || []);
      s.has(waarde) ? s.delete(waarde) : s.add(waarde);
      if (s.size) next[key] = s; else delete next[key];
      return next;
    });
  };

  const zetSort = (key) => {
    setSort(prev => (!prev || prev.key !== key) ? { key, dir: 'asc' }
      : prev.dir === 'asc' ? { key, dir: 'desc' } : null);
  };

  const SORTEER = {
    id: p => p.put_id,
    coord: p => p.x_rd,
    gemeten_diepte_m: p => p.gemeten_diepte_m ?? -1,
    opgegeven_diepte_m: p => p.opgegeven_diepte_m ?? -1,
    max_diepte_mv: p => p.max_diepte_mv ?? -1,
    diepte_bv_filter_m: p => p.diepte_bv_filter_m ?? -1,
    perceel: p => p.perceel || '',
    gebieden: p => gebiedenKort(p).join(','),
    waterstaatswerken: p => wswKort(p).join(','),
    houder: p => p.houder_naam || '',
    status: p => p._status,
  };

  const sorteer = (rijen) => {
    if (!sort) return rijen;
    const f = SORTEER[sort.key];
    if (!f) return rijen;
    return [...rijen].sort((a, b) => {
      const va = f(a), vb = f(b);
      const c = (typeof va === 'number' && typeof vb === 'number')
        ? va - vb
        : String(va).localeCompare(String(vb), 'nl', { numeric: true });
      return sort.dir === 'asc' ? c : -c;
    });
  };

  /* Status één keer vooraf bepalen, zodat filter en sorteer dezelfde bron zien. */
  const metStatus = (lijst, isAfgedicht) =>
    lijst.map(p => ({ ...p, _status: statusLabel(p, isAfgedicht) }));

  const actiefRuw = metStatus(putten.filter(p => !p.buiten_gebruik_datum), false);
  const afgedichtRuw = metStatus(putten.filter(p => p.buiten_gebruik_datum), true);
  const actief = sorteer(actiefRuw.filter(p => doorFilters(p, null)));
  const afgedicht = sorteer(afgedichtRuw.filter(p => doorFilters(p, null)));

  const telWaarden = (key) => {
    const f = PANEEL_FACETTEN.find(x => x.key === key);
    const tel = new Map();
    for (const p of [...actiefRuw, ...afgedichtRuw]) {
      if (!doorFilters(p, key)) continue;
      for (const v of f.waarde(p)) tel.set(v, (tel.get(v) || 0) + 1);
    }
    return [...tel.entries()].sort((a, b) =>
      b[1] - a[1] || a[0].localeCompare(b[0], 'nl', { numeric: true }));
  };

  const aantalNietGekoppeld = actiefRuw.filter(p => p._status === 'Niet gekoppeld').length;
  const aantalAfdichting = actiefRuw.filter(p => p._status === 'Afdichtingsprocedure').length;
  const filterActief = zoek.trim() !== '' || Object.keys(filters).length > 0
    || Object.keys(kopzoek).length > 0;

  /* ── bouwstenen ───────────────────────────────────────────────────────── */

  const KOLOMMEN = [
    { key: 'id', label: 'ID' },
    { key: 'coord', label: 'RD x, y', loep: 'coord' },
    ...(toonDiepteDetails ? [
      { key: 'gemeten_diepte_m', label: 'Gem. diepte' },
      { key: 'opgegeven_diepte_m', label: 'Opg. diepte' },
      { key: 'max_diepte_mv', label: 'Max diepte' },
      { key: 'diepte_bv_filter_m', label: 'Filter bv' },
    ] : []),
    { key: 'perceel', label: 'Perceel', loep: 'perceel' },
    { key: 'gebieden', label: 'Kwetsbare gebieden' },
    ...(toonWaterstaatswerken
      ? [{ key: 'waterstaatswerken', label: 'Waterstaatswerken' }] : []),
    { key: 'houder', label: 'Houder', loep: 'houder' },
    { key: 'status', label: 'Status' },
    { key: 'boorstatus', label: 'Boorstatus' },
  ];

  const renderKop = () => (
    <tr>
      {KOLOMMEN.map(k => {
        const gesorteerd = sort && sort.key === k.key;
        const veldOpen = k.loep && (kopOpen === k.loep || kopzoek[k.loep]);
        return (
          <th key={k.key}>
            <span className="pl-kopcel">
              <button
                type="button"
                className={`pl-koplabel${gesorteerd ? ' gesorteerd' : ''}`}
                onClick={() => zetSort(k.key)}
                title="Klik om te sorteren"
              >
                {k.label}
                <span className="pl-pijl">
                  {gesorteerd ? PIJLEN[sort.dir] : PIJLEN.geen}
                </span>
              </button>
              {k.loep && (veldOpen ? (
                <input
                  className="pl-kopveld"
                  autoFocus={kopOpen === k.loep}
                  value={kopzoek[k.loep] || ''}
                  placeholder="Zoek…"
                  onChange={(e) => {
                    const v = e.target.value;
                    setKopzoek(prev => {
                      const next = { ...prev };
                      if (v.trim()) next[k.loep] = v.trim(); else delete next[k.loep];
                      return next;
                    });
                  }}
                  onBlur={() => { if (!kopzoek[k.loep]) setKopOpen(null); }}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                      setKopzoek(prev => {
                        const next = { ...prev }; delete next[k.loep]; return next;
                      });
                      setKopOpen(null);
                    }
                  }}
                />
              ) : (
                <button
                  type="button"
                  className="pl-kopzoek"
                  title={`Zoek in ${k.label}`}
                  onClick={() => setKopOpen(k.loep)}
                ><LoepIcoon /></button>
              ))}
            </span>
          </th>
        );
      })}
    </tr>
  );

  const renderGebiedChips = (p, lijst, facetKey) => {
    if (!lijst || lijst.length === 0) return <span className="pl-leeg">–</span>;
    return (
      <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 4 }}>
        {lijst.map(g => {
          const s = gebiedStijl(g.type);
          const tekst = s.kort || g.naam;
          return (
            <span
              key={g.gebied_id}
              className="pl-chip-g"
              title={`${g.naam} — klik om hierop te filteren`}
              onClick={(e) => { e.stopPropagation(); zetFilter(facetKey, tekst); }}
              style={{ background: s.chipBg, color: s.chipText, border: `1px solid ${s.stroke}` }}
            >{tekst}</span>
          );
        })}
      </span>
    );
  };

  /* Zelfde bouwstenen als renderStatus hiernaast: stip, klik om te filteren.
     Twee kolommen die op elkaar lijken maar iets anders zeggen -- Status gaat
     over de koppeling aan een gebruiksrecht, Boorstatus over de put in de
     grond. Daarom staan ze naast elkaar en niet in één kolom. */
  const renderBoorstatus = (p) => {
    const label = p.boorstatus || 'Onbekend';
    const kleur = AANLEG_KLEUR[label];
    return (
      <span
        className="pl-status"
        title="Klik om op deze boorstatus te filteren"
        onClick={(e) => { e.stopPropagation(); zetFilter('boorstatus', label); }}
      >
        <span className={`pl-dot${kleur ? '' : ' hol pl-dot-onbekend'}`}
              style={{ background: kleur || 'transparent' }} />
        {label}
      </span>
    );
  };

  const renderStatus = (p, isAfgedicht) => {
    const label = p._status;
    const toekomstig = label === 'Gekoppeld (toekomstig)';
    const titel = isAfgedicht ? 'Klik om op deze status te filteren'
      : label === 'Afdichtingsprocedure'
        ? `Afdichtingsprocedure gestart op ${fmtDatumNL(p.afdichting_gestart_datum)} — klik om te filteren`
        : toekomstig
          ? `Gekoppeld aan een gebruiksrecht dat ingaat op ${fmtDatumNL(p.gekoppeld_vanaf)} — klik om te filteren`
          : 'Klik om op deze status te filteren';
    return (
      <span>
        <span
          className="pl-status"
          title={titel}
          onClick={(e) => { e.stopPropagation(); zetFilter('status', label); }}
        >
          <span
            className={`pl-dot${toekomstig ? ' hol' : ''}`}
            style={{ background: toekomstig ? 'transparent' : STATUS_KLEUR[label] }}
          />
          {isAfgedicht ? `Afgedicht ${fmtDatumNL(p.buiten_gebruik_datum)}`
            : toekomstig ? <>Gekoppeld <Klokje grootte={12} /></>
            : label}
        </span>
        {(label === 'Niet gekoppeld' || label === 'Afdichtingsprocedure')
          && p.laatste_houder_naam && (
          <div className="pl-laatst" onClick={(e) => e.stopPropagation()}>
            laatst:{' '}
            <Link to={`/beregening/houders/${p.laatste_houder_id}`}
                  style={{ color: 'var(--text-soft)' }}>
              {p.laatste_houder_naam}
            </Link>
            {p.laatste_eind_datum && ` · ${fmtDatumNL(p.laatste_eind_datum)}`}
          </div>
        )}
      </span>
    );
  };

  const renderRow = (p, isAfgedicht) => {
    const t = zoekTreffer(p);
    const q = zoek.trim();
    const kz = kopzoek;
    const coordTekst = `${Math.round(p.x_rd)}, ${Math.round(p.y_rd)}`;
    return (
      <tr
        key={p.put_id}
        className="clickable"
        onClick={() => navigate(`/beregening/putten/${p.put_id}`)}
        style={isAfgedicht ? { opacity: 0.6 } : undefined}
      >
        <td className="pl-cel-id">
          <span className="mono mono-id">
            {t.waar === 'id' ? markeer(fmtId(p.put_id), q) : fmtId(p.put_id)}
          </span>
          {t.waar === 'migratie' && (
            <span className="pl-merk" title={t.regel}>datamigratie</span>
          )}
        </td>
        <td className="pl-cel-dim">
          {kz.coord ? markeer(coordTekst, kz.coord)
            : t.waar === 'coord' ? markeer(coordTekst, q) : coordTekst}
        </td>
        {toonDiepteDetails && <td>{p.gemeten_diepte_m ?? <span className="pl-leeg">–</span>}</td>}
        {toonDiepteDetails && <td>{p.opgegeven_diepte_m ?? <span className="pl-leeg">–</span>}</td>}
        {toonDiepteDetails && <td>{p.max_diepte_mv ?? <span className="pl-leeg">–</span>}</td>}
        {toonDiepteDetails && <td>{p.diepte_bv_filter_m ?? <span className="pl-leeg">–</span>}</td>}
        <td className="pl-cel-mono">
          {p.perceel
            ? (kz.perceel ? markeer(p.perceel, kz.perceel)
              : t.waar === 'perceel' ? markeer(p.perceel, q) : p.perceel)
            : <span className="pl-leeg">–</span>}
        </td>
        <td>{renderGebiedChips(p, p.gebieden, 'gebieden')}</td>
        {toonWaterstaatswerken && (
          <td>{renderGebiedChips(p, p.waterstaatswerken, 'waterstaatswerken')}</td>
        )}
        <td>
          {p.houder_naam
            ? (kz.houder ? markeer(p.houder_naam, kz.houder)
              : t.waar === 'houder' ? markeer(p.houder_naam, q) : p.houder_naam)
            : <span className="pl-leeg">–</span>}
        </td>
        <td>{renderStatus(p, isAfgedicht)}</td>
        <td>{renderBoorstatus(p)}</td>
      </tr>
    );
  };

  /* Gefilterd-regel: paneelfilters én kopzoekvelden, elk met een kruisje. */
  const chips = [];
  for (const f of PANEEL_FACETTEN) {
    if (filters[f.key]) {
      for (const v of filters[f.key]) {
        chips.push({
          label: f.label, tekst: v,
          weg: () => zetFilter(f.key, v),
        });
      }
    }
  }
  for (const [k, z] of Object.entries(kopzoek)) {
    chips.push({
      label: KOPZOEK_VELDEN[k].label, tekst: `bevat “${z}”`,
      weg: () => {
        setKopzoek(prev => { const next = { ...prev }; delete next[k]; return next; });
        setKopOpen(null);
      },
    });
  }

  const aantalFilters = Object.values(filters).reduce((a, s) => a + s.size, 0)
    + Object.keys(kopzoek).length;

  return (
    <div className={`main-inner${toonWaterstaatswerken ? ' main-inner-wide' : ''}`}>
      <div className="page-header">
        <div>
          <h1 className="page-title">Putten</h1>
          <p className="page-subtitle">
            {actiefRuw.length} actief
            {afgedichtRuw.length > 0 && ` · ${afgedichtRuw.length} afgedicht`}
            {aantalAfdichting > 0 && ` · ${aantalAfdichting} in afdichtingsprocedure`}
            {aantalNietGekoppeld > 0 && ` · ${aantalNietGekoppeld} niet gekoppeld`}
            {filterActief && ` · ${actief.length + afgedicht.length} na filter`}
          </p>
        </div>
        <div className="row" style={{ gap: 8 }}>
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => setToonDiepteDetails(v => !v)}
            title="Toon of verberg diepte-kolommen (werkelijke diepte, max diepte, filter bv)"
          >
            {toonDiepteDetails ? '✓ Diepte-details' : 'Toon diepte-details'}
          </button>
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => setToonWaterstaatswerken(v => !v)}
            title="Toon of verberg waterstaatswerken-kolom (waterkeringen, beschermingszones, oppervlaktewater)"
          >
            {toonWaterstaatswerken ? '✓ Waterstaatswerken' : 'Toon waterstaatswerken'}
          </button>
          <button className="btn btn-primary" onClick={() => navigate('/beregening/putten/nieuw')}>
            Nieuwe put
          </button>
        </div>
      </div>

      <div className="pl-balk">
        <div className="pl-zoek">
          <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8"
               strokeLinecap="round">
            <circle cx="8.5" cy="8.5" r="5.5" /><path d="M12.8 12.8 17 17" />
          </svg>
          <input
            type="search"
            value={zoek}
            onChange={(e) => setZoek(e.target.value)}
            placeholder="Zoek op putnummer, coördinaat, perceel, houder of oud nummer"
          />
          {zoek && (
            <button className="pl-wis" title="Wissen" onClick={() => setZoek('')}>×</button>
          )}
        </div>
        <button
          className="pl-filterknop"
          onClick={() => setPaneelOpen(v => !v)}
        >
          <svg viewBox="0 0 14 14" fill="currentColor">
            <path d="M1.2 1h11.6c.5 0 .8.6.5 1L8.8 7.4V12c0 .3-.15.5-.4.6l-2 1c-.4.2-.8-.1-.8-.5V7.4L1.1 2c-.3-.4 0-1 .5-1z" />
          </svg>
          Filters
          {aantalFilters > 0 && <span className="pl-badge">{aantalFilters}</span>}
        </button>
      </div>

      {chips.length > 0 && (
        <div className="pl-chips">
          <span className="pl-chips-lbl">Gefilterd:</span>
          {chips.map((c, i) => (
            <span key={i} className="pl-fchip">
              <span className="veld">{c.label}</span>
              <b>{c.tekst}</b>
              <button onClick={c.weg} title="Dit filter weghalen">×</button>
            </span>
          ))}
          <button
            className="pl-alleswis"
            onClick={() => { setFilters({}); setKopzoek({}); setKopOpen(null); }}
          >Alles wissen</button>
        </div>
      )}

      {loading ? (
        <div className="empty-state"><span className="spinner" /></div>
      ) : (
        <>
          {actiefRuw.length === 0 ? (
            <div className="empty-state">Geen putten.</div>
          ) : (
            <div className="table-wrap">
              <table className="table pl-tabel">
                <thead>{renderKop()}</thead>
                <tbody>
                  {actief.length === 0 ? (
                    <tr><td colSpan={KOLOMMEN.length} style={{ textAlign: 'center', color: 'var(--text-muted)', fontStyle: 'italic', padding: 32 }}>
                      Geen rijen voldoen aan de filter.
                    </td></tr>
                  ) : actief.map(p => renderRow(p, false))}
                </tbody>
              </table>
            </div>
          )}

          {afgedichtRuw.length > 0 && (
            <div style={{ marginTop: 24 }}>
              <button
                className="btn btn-ghost"
                onClick={() => setAfgedichtOpen(!afgedichtOpen)}
                style={{ marginBottom: afgedichtOpen ? 12 : 0 }}
              >
                {afgedichtOpen ? '▴' : '▾'} Afgedichte putten ({afgedicht.length})
              </button>
              {afgedichtOpen && (
                <div className="table-wrap">
                  <table className="table pl-tabel">
                    <thead>{renderKop()}</thead>
                    <tbody>
                      {afgedicht.length === 0 ? (
                        <tr><td colSpan={KOLOMMEN.length} style={{ textAlign: 'center', color: 'var(--text-muted)', fontStyle: 'italic', padding: 32 }}>
                          Geen rijen voldoen aan de filter.
                        </td></tr>
                      ) : afgedicht.map(p => renderRow(p, true))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* ── filterpaneel ── */}
      <div
        className={`pl-sluier${paneelOpen ? ' open' : ''}`}
        onClick={() => setPaneelOpen(false)}
      />
      <aside className={`pl-paneel${paneelOpen ? ' open' : ''}`} aria-label="Filters">
        <div className="pl-paneel-kop">
          <b>Filters</b>
          <button className="pl-dicht" title="Sluiten" onClick={() => setPaneelOpen(false)}>×</button>
        </div>
        <div className="pl-paneel-lijf">
          {PANEEL_FACETTEN.map(f => {
            const waarden = telWaarden(f.key);
            const gekozen = filters[f.key] || new Set();
            return (
              <div className="pl-sectie" key={f.key}>
                <h4>{f.label}</h4>
                {f.noot && waarden.length <= 1 && (
                  <div className="pl-noot">{f.noot}</div>
                )}
                {waarden.length === 0 && <div className="pl-noot">geen waarden</div>}
                {waarden.map(([v, n]) => (
                  <label className="pl-check" key={v}>
                    <input
                      type="checkbox"
                      checked={gekozen.has(v)}
                      onChange={() => zetFilter(f.key, v)}
                    />
                    <span>{v}</span>
                    <span className="n">{n}</span>
                  </label>
                ))}
              </div>
            );
          })}
        </div>
        <div className="pl-paneel-voet">
          <button className="pl-wisalles" onClick={() => setFilters({})}>Alles wissen</button>
          <button className="pl-klaar" onClick={() => setPaneelOpen(false)}>Klaar</button>
        </div>
      </aside>
    </div>
  );
}
